import React, { useState, useEffect } from 'react';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DefaultModal from 'components/Utils/DefaultModal';
import Icon from 'components/Utils/NewIcon';
import { TestHookActionPayload } from 'generated/api/models/TestHookActionPayload';
import './style.scss';
import { EventParameter, EventSchema, HookAction } from '../../../generated/api';
import { HookActionTypes } from '../Helpers';
import { useAppSelector } from '../../../careand-redux/store';
import { getDefaultValue } from 'components/Utils/ParameterEditor/typeUtils';

interface TestParameterModalProps {
  eventSchema: EventSchema | null;
  onClose: () => void;
  action: HookAction;
  hookId?: string;
}

const TestParameterModal: React.FC<TestParameterModalProps> = ({
  eventSchema,
  onClose,
  action,
  hookId,
}) => {
  const dispatch = useAppDispatch();
  const [testEventData, setTestEventData] = useState<{ [key: string]: any }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const ownUser = useAppSelector((state) => state.authentication?.user);

  const ownPhone = ownUser?.phone;
  const ownEmail = ownUser?.email;

  // Initialize test data when modal opens or eventSchema changes
  useEffect(() => {
    if (eventSchema?.parameters) {
      const initialTestData = eventSchema.parameters.reduce(
        (acc, param) => {
          // Convert example value to proper type
          let value = getDefaultValue(param.example, param.type);

          // Override with user's own email/phone if applicable
          if (ownEmail && param.name.includes('email')) value = ownEmail;
          if (ownPhone && param.name.includes('phone')) value = ownPhone;

          acc[param.name] = value;
          return acc;
        },
        {} as { [key: string]: any },
      );
      setTestEventData(initialTestData);
    }
  }, [eventSchema, ownEmail, ownPhone]);

  // Helper function to trim config values before testing
  const trimActionConfig = (config: any): any => {
    if (!config || typeof config !== 'object') return config;

    const trimmed = { ...config };
    Object.keys(trimmed).forEach((key) => {
      if (typeof trimmed[key] === 'string') {
        trimmed[key] = trimmed[key].trim();
      }
    });

    return trimmed;
  };

  const handleParameterChange = (paramName: string, value: any) => {
    setTestEventData((prev) => ({
      ...prev,
      [paramName]: value,
    }));
  };

  // Handle text/number field changes
  const handleTextChange = (paramName: string, value: string, paramType: string) => {
    let finalValue: any = value;

    // Convert to number if it's a numeric type
    if (
      paramType.toLowerCase() === 'number' ||
      paramType.toLowerCase() === 'integer' ||
      paramType.toLowerCase() === 'float'
    ) {
      const num = Number(value);
      if (!isNaN(num) && value.trim() !== '') {
        finalValue = num;
      }
    }

    handleParameterChange(paramName, finalValue);
  };

  // Handle toggling boolean values
  const toggleBoolean = (paramName: string) => {
    setTestEventData((prev) => ({
      ...prev,
      [paramName]: !prev[paramName],
    }));
  };

  const handleTest = async () => {
    if (!action || !hookId) {
      console.warn('Cannot test action: missing action or hook ID');
      return;
    }

    setIsLoading(true);

    try {
      // Create the payload using the updated API interface
      // IMPORTANT: Convert all values to strings as the backend expects
      const testPayload: TestHookActionPayload = {
        mock_event_data: eventSchema?.parameters.map((param) => {
          return {
            ...param,
            value: String(testEventData[param.name]), // Convert to string
          } as EventParameter;
        }),
      };

      testPayload.action_override = {
        type: action.type,
        name: action.name || `Test ${action.type} Action`,
        enabled: action.enabled ?? true,
        config: trimActionConfig(action.config), // Trim whitespace from config values
      };

      await dispatch(
        api.Hooks.testHookAction({
          hookId,
          testHookActionPayload: testPayload,
        }),
        {
          title: 'Test Action',
        },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getActionInfo = () => {
    const actionType = HookActionTypes.find((t) => t.value === action.type);
    return {
      label: actionType?.label || action.type,
      icon: actionType?.icon,
    };
  };

  const actionInfo = getActionInfo();

  // Group parameters by entity (first part before the dot)
  const groupedParameters = () => {
    if (!eventSchema?.parameters) return {};

    const grouped: { [entity: string]: EventParameter[] } = {};

    eventSchema.parameters.forEach((param) => {
      const entity = param.name.includes('.') ? param.name.split('.')[0]! : 'general';
      if (!grouped[entity]) {
        grouped[entity] = [];
      }
      grouped[entity]!.push(param);
    });

    return grouped;
  };

  // Convert entity name to title case (e.g., "oldAppointment" -> "Old Appointment")
  const entityToTitle = (entity: string): string => {
    return entity
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, (s) => s.toUpperCase()) // Capitalize first letter
      .trim();
  };

  // Render parameter field based on type
  const renderParameterField = (param: EventParameter) => {
    const currentValue = testEventData[param.name];

    // BOOLEAN TYPE: Toggle button with check/X icon
    if (param.type.toLowerCase() === 'boolean') {
      return (
        <div className="test-parameter-field boolean-field" key={param.name}>
          <label className="parameter-label">{param.description}</label>
          <DefaultButton
            name={String(currentValue)}
            icon={currentValue ? 'check' : 'times'}
            onClick={() => toggleBoolean(param.name)}
            color={currentValue ? 'green' : 'red'}
            className="boolean-toggle-button"
          />
        </div>
      );
    }

    // ENUM TYPE: Dropdown select
    if (param.enum_values && param.enum_values.length > 0) {
      return (
        <div className="test-parameter-field enum-field" key={param.name}>
          <DefaultSelectField
            label={param.description}
            value={String(currentValue)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleParameterChange(param.name, e.target.value)
            }
            inputs={param.enum_values}
          />
        </div>
      );
    }

    // STRING/NUMBER TYPE: Standard text field
    return (
      <div className="test-parameter-field text-field" key={param.name}>
        <DefaultTextField
          label={param.description}
          value={String(currentValue)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleTextChange(param.name, e.target.value, param.type)
          }
          placeholder={param.example || 'Enter value...'}
          type={
            param.type.toLowerCase() === 'number' ||
            param.type.toLowerCase() === 'integer' ||
            param.type.toLowerCase() === 'float'
              ? 'number'
              : 'text'
          }
        />
      </div>
    );
  };

  const modalContent = (
    <div className="test-parameter-modal-content">
      <div className="test-modal-header">
        <div className="test-action-info">
          {!action?.id && <span className="unsaved-indicator">(Unsaved Configuration)</span>}
        </div>
        <p className="test-modal-description">
          Override the event parameter values below to test your current action configuration before
          saving.
        </p>
      </div>

      <div className="test-parameters">
        {eventSchema?.parameters && eventSchema.parameters.length > 0 ? (
          <>
            {Object.entries(groupedParameters()).map(([entity, params]) => (
              <div className="entity-group" key={entity}>
                <h4 className="entity-title">{entityToTitle(entity)}</h4>
                {params.map((param) => renderParameterField(param))}
              </div>
            ))}
          </>
        ) : (
          <div className="no-parameters">
            <Icon src="info-circle" />
            <p>No parameters available for this event type.</p>
          </div>
        )}
      </div>

      <div className="test-modal-footer">
        <DefaultButton
          name={isLoading ? 'Testing...' : 'Run Test'}
          icon="play"
          onClick={handleTest}
          className="blue"
          loading={isLoading}
          disabled={isLoading}
          style={{ marginLeft: '8px' }}
        />
      </div>
    </div>
  );

  return (
    <DefaultModal
      headerContent={
        <div className="row gap-2 items-center">
          <Icon src={actionInfo.icon!} height="20px" />
          <span>Testing {actionInfo.label} Action</span>
        </div>
      }
      className="test-parameter-modal"
      content={modalContent}
      wrap={true}
      padding={true}
      isOpen={true}
      onRequestClose={onClose}
    />
  );
};

export default TestParameterModal;
